import { useMutation, useQuery } from "@apollo/client/react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Alert, App, Button, Flex, Form, Input, Modal, Space, Table, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CancelTeamInvitationDocument,
  ChangeTeamMemberRoleDocument,
  GetPendingTeamInvitationsDocument,
  GetTeamMembersDocument,
  InviteTeamMemberDocument,
  RemoveTeamMemberDocument,
  TeamRole,
} from "../../gql/generated/graphqlSchema.tsx";
import { useCurrentSession } from "../../context/CurrentSessionContext.tsx";
import { TableEmptyState } from "../../components/TableEmptyState.tsx";
import { useServiceError } from "../../hooks/useServiceError.ts";
import { formNarrow, title } from "../../styles/shared.styles.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { normalizeString } from "../../utils/stringNormalize.ts";
import { RoleCapabilitiesHelp } from "./RoleCapabilitiesHelp.tsx";

type InviteFormValues = { email: string };

export const TeamSection = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const [inviteOpen, setInviteOpen] = useState(false);

  const roleLabel = (role: TeamRole): string =>
    role === TeamRole.Admin ? t("team.roles.ADMIN") : t("team.roles.MEMBER");

  const { user: currentUser } = useCurrentSession();
  const isAdmin = currentUser.membership?.role === TeamRole.Admin;

  const { data: membersData, loading: membersLoading, refetch: refetchMembers } = useQuery(GetTeamMembersDocument);
  // pendingTeamInvitations is admin-only on the server; skip the call for
  // non-admins instead of letting it 401 / throw.
  const {
    data: invitationsData,
    loading: invitationsLoading,
    refetch: refetchInvitations,
  } = useQuery(GetPendingTeamInvitationsDocument, { skip: !isAdmin });

  const members = membersData?.teamMembers ?? [];
  const invitations = invitationsData?.pendingTeamInvitations ?? [];
  const adminCount = members.filter((m) => m.role === TeamRole.Admin).length;

  const [inviteMember, { loading: inviting, error: inviteError, reset: resetInviteMutation }] = useMutation(
    InviteTeamMemberDocument,
    {
      onCompleted: (res) => {
        void message.success(t("team.invited", { email: res.inviteMember.email }));
        void refetchInvitations();
      },
    },
  );
  const inviteErrorMessage = useServiceError(inviteError);
  const [cancelInvitation] = useMutation(CancelTeamInvitationDocument, {
    onCompleted: () => {
      void refetchInvitations();
    },
  });
  const [removeMember] = useMutation(RemoveTeamMemberDocument, {
    onCompleted: () => {
      void refetchMembers();
    },
  });
  const [changeRole] = useMutation(ChangeTeamMemberRoleDocument, {
    onCompleted: () => {
      void message.success(t("team.roleUpdated"));
      void refetchMembers();
    },
  });

  const [inviteForm] = Form.useForm<InviteFormValues>();

  const closeInviteModal = () => {
    setInviteOpen(false);
    inviteForm.resetFields();
    resetInviteMutation();
  };

  const onInvite = async (values: InviteFormValues) => {
    const result = await inviteMember({
      variables: { input: { email: normalizeString(values.email) } },
    });
    if (result.data?.inviteMember) {
      closeInviteModal();
    }
  };

  const copyInviteLink = async (inviteUrl: string) => {
    await navigator.clipboard.writeText(inviteUrl);
    void message.success(t("team.inviteLinkCopied"));
  };

  const canDemoteOrRemove = (targetRole: TeamRole) => targetRole !== TeamRole.Admin || adminCount > 1;

  return (
    <Flex gap="large" vertical>
      {isAdmin ? (
        <div>
          <Button icon={<UserAddOutlined />} onClick={() => setInviteOpen(true)} type="primary">
            {t("team.inviteMember")}
          </Button>
        </div>
      ) : null}

      <Flex gap="small" vertical>
        <Typography.Title className={title} level={4}>
          {t("team.members")}
        </Typography.Title>
        <Table
          bordered
          columns={[
            {
              title: t("common.name"),
              key: "name",
              render: (_, record) => record.user.name,
            },
            {
              title: t("common.email"),
              key: "email",
              render: (_, record) => record.user.email,
            },
            {
              title: t("team.role"),
              key: "role",
              width: 140,
              render: (_, record) => roleLabel(record.role),
            },
            {
              title: t("team.joinedAt"),
              key: "joinedAt",
              render: (_, record) => formatDateTime(record.joinedAt, i18n.language),
            },
            {
              title: t("g.actions"),
              key: "actions",
              fixed: "right",
              width: 1,
              render: (_, record) => {
                const isSelf = record.userId === currentUser.id;
                const targetIsAdmin = record.role === TeamRole.Admin;
                const newRole = targetIsAdmin ? TeamRole.Member : TeamRole.Admin;
                const changeRoleLabel = targetIsAdmin ? t("team.demoteToMember") : t("team.promoteToAdmin");
                const changeRoleConfirm = targetIsAdmin
                  ? t("team.demoteToMemberConfirm", { name: record.user.name })
                  : t("team.promoteToAdminConfirm", { name: record.user.name });
                const changeRoleDisabledReason = !isAdmin
                  ? t("team.actionAdminOnly")
                  : isSelf
                    ? t("team.actionCannotActOnSelf")
                    : targetIsAdmin && !canDemoteOrRemove(record.role)
                      ? t("team.actionLastAdmin")
                      : null;
                const removeDisabledReason = !isAdmin
                  ? t("team.actionAdminOnly")
                  : isSelf
                    ? t("team.actionCannotActOnSelf")
                    : !canDemoteOrRemove(record.role)
                      ? t("team.actionLastAdmin")
                      : null;
                return (
                  <Space>
                    <Tooltip destroyOnHidden title={changeRoleDisabledReason ?? changeRoleLabel}>
                      <Button
                        disabled={changeRoleDisabledReason != null}
                        icon={targetIsAdmin ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                        onClick={() =>
                          modal.confirm({
                            title: changeRoleConfirm,
                            content: <RoleCapabilitiesHelp />,
                            okButtonProps: { icon: targetIsAdmin ? <ArrowDownOutlined /> : <ArrowUpOutlined /> },
                            cancelButtonProps: { icon: <CloseOutlined /> },
                            okText: changeRoleLabel,
                            onOk: () => changeRole({ variables: { input: { userId: record.userId, role: newRole } } }),
                          })
                        }
                      />
                    </Tooltip>
                    <Tooltip destroyOnHidden title={removeDisabledReason ?? t("team.removeMember")}>
                      <Button
                        danger
                        disabled={removeDisabledReason != null}
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          modal.confirm({
                            title: t("team.removeMember"),
                            content: t("team.removeMemberConfirm", { name: record.user.name }),
                            okButtonProps: { danger: true, icon: <DeleteOutlined /> },
                            cancelButtonProps: { icon: <CloseOutlined /> },
                            okText: t("team.removeMember"),
                            onOk: () =>
                              removeMember({ variables: { userId: record.userId } }).then(
                                () => void message.success(t("team.memberRemoved", { name: record.user.name })),
                              ),
                          })
                        }
                      />
                    </Tooltip>
                  </Space>
                );
              },
            },
          ]}
          dataSource={members}
          loading={membersLoading}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Flex>

      {isAdmin ? (
        <Flex gap="small" vertical>
          <Typography.Title className={title} level={4}>
            {t("team.pendingInvitations")}
          </Typography.Title>
          <Table
            bordered
            columns={[
              {
                title: t("common.email"),
                key: "email",
                render: (_, record) => record.email,
              },
              {
                title: t("team.expiresAt"),
                key: "expiresAt",
                render: (_, record) => formatDateTime(record.expiresAt, i18n.language),
              },
              {
                title: t("g.actions"),
                key: "actions",
                fixed: "right",
                width: 1,
                render: (_, record) => (
                  <Space>
                    <Tooltip destroyOnHidden title={t("team.copyInviteLink")}>
                      <Button icon={<CopyOutlined />} onClick={() => void copyInviteLink(record.inviteUrl)} />
                    </Tooltip>
                    <Tooltip destroyOnHidden title={t("team.cancelInvitation")}>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          modal.confirm({
                            title: t("team.cancelInvitation"),
                            content: t("team.cancelInvitationConfirm", { email: record.email }),
                            okButtonProps: { danger: true, icon: <DeleteOutlined /> },
                            cancelButtonProps: { icon: <CloseOutlined /> },
                            okText: t("team.cancelInvitation"),
                            onOk: () => cancelInvitation({ variables: { invitationId: record.id } }),
                          })
                        }
                      />
                    </Tooltip>
                  </Space>
                ),
              },
            ]}
            dataSource={invitations}
            loading={invitationsLoading}
            locale={{
              emptyText: (
                <TableEmptyState
                  emptyChildren={
                    <Button
                      color="primary"
                      icon={<UserAddOutlined />}
                      onClick={() => setInviteOpen(true)}
                      variant="outlined"
                    >
                      {t("team.inviteMember")}
                    </Button>
                  }
                  emptyDescription={t("team.noPendingInvitations")}
                  loading={invitationsLoading}
                />
              ),
            }}
            pagination={false}
            rowKey="id"
            size="small"
          />
        </Flex>
      ) : null}

      <Modal
        cancelButtonProps={{ icon: <CloseOutlined /> }}
        confirmLoading={inviting}
        destroyOnHidden
        okButtonProps={{ icon: <UserAddOutlined /> }}
        okText={t("team.sendInvite")}
        onCancel={closeInviteModal}
        onOk={() => inviteForm.submit()}
        open={inviteOpen}
        title={t("team.inviteMember")}
      >
        <Form<InviteFormValues>
          className={formNarrow}
          form={inviteForm}
          layout="vertical"
          onFinish={onInvite}
          requiredMark={false}
        >
          <Form.Item
            label={t("common.email")}
            name="email"
            rules={[
              { required: true, message: t("team.validation.emailRequired") },
              { type: "email", message: t("team.validation.emailInvalid") },
            ]}
          >
            <Input />
          </Form.Item>
          {inviteErrorMessage != null ? <Alert showIcon title={inviteErrorMessage} type="error" /> : null}
        </Form>
      </Modal>
    </Flex>
  );
};
